import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListResponseOfStudyField,
} from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";

type SelectStudyFieldProps = {
  value?: string | null;
  onChange: (id: string | null) => void;
  onDirty?: () => void;
  onClean?: () => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectStudyField = ({
  value: studyFieldId,
  onChange,
  onDirty,
  onClean,
  ...rest
}: SelectStudyFieldProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [studyField, setStudyField] = useState<IdValue | null>(null);

  // Load the initial value if we're passed a studyFieldId
  const { isLoading, data } = useQuery<ListResponseOfStudyField>(
    ["studyFieldById", studyFieldId],
    () =>
      skuService.getStudyFields(
        AdminListRequestOfBaseFilter.fromJS({ sortField: "name" })
      ),
    {
      enabled:
        !!studyFieldId && (!studyField || studyField.id !== studyFieldId),
    }
  );

  useEffect(() => {
    // Once the initial value loads, update the value (if is hasn't been set by a search)
    if (data?.data?.[0]) {
      setStudyField({
        id: data!.data![0].id,
        value: data!.data![0].name ?? "?",
      });
    }
  }, [data, studyField]);

  const fetchStudyFields = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfStudyField>(
      ["studyFields", searchTerm],
      () =>
        skuService.getStudyFields(
          new AdminListRequestOfBaseFilter({
            pageNumber: 0,
            pageSize: 20,
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
            sortField: "name",
          })
        )
    );
    // We need to massage these results into the standard format

    const levels = {
      ...result,
      data: (result.data ?? []).map(({ id, name }) => ({
        id,
        value: name ?? "?",
      })),
    };

    return { result: levels, forSearchTerm: searchTerm };
  };

  const onChangeStudyField = (studyField: IdValue | null) => {
    setStudyField(studyField);
    onChange(studyField?.id ?? null);
    if (studyField !== null) {
      if(onDirty){
        onDirty();
      }
    } else {
      if(onClean){
        onClean();
      }
    }
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={studyField}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      label={t({ id: "skuStudyFieldLabel" })}
      fetchData={fetchStudyFields}
      failMessage={t({ id: "selectProductFetchFailed" })}
      onChange={onChangeStudyField}
    />
  );
};

export default SelectStudyField;
